/**
 * Formats a file size in bytes to a human-readable string.
 *
 * @param bytes - The file size in bytes
 * @param decimals - Number of decimal places (default: 2)
 * @param binary - Whether to use binary (1024) or decimal (1000) units (default: false for decimal)
 * @returns Formatted file size string (e.g., "1.5 MB", "500 KB", "2.3 GB")
 *
 * @example
 * formatFileSize(1024) // "1 KB"
 * formatFileSize(1536) // "1.5 KB"
 * formatFileSize(1048576) // "1 MB"
 * formatFileSize(1073741824) // "1 GB"
 * formatFileSize(1536, 1) // "1.5 KB"
 * formatFileSize(1048576, 2, true) // "1 MB" (binary)
 */
export const formatFileSize = (
  bytes: number,
  decimals: number = 2,
  binary: boolean = false
): string => {
  if (bytes === 0) return "0 Bytes";

  const k = binary ? 1024 : 1000;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = binary
    ? ["Bytes", "KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"]
    : ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

/**
 * Formats a file size in bytes to a human-readable string with appropriate precision.
 * Automatically determines the best number of decimal places based on the size.
 *
 * @param bytes - The file size in bytes
 * @param binary - Whether to use binary (1024) or decimal (1000) units (default: false for decimal)
 * @returns Formatted file size string with smart precision
 *
 * @example
 * formatFileSizeSmart(1024) // "1 KB"
 * formatFileSizeSmart(1536) // "1.5 KB"
 * formatFileSizeSmart(1048576) // "1 MB"
 * formatFileSizeSmart(1572864) // "1.5 MB"
 */
export const formatFileSizeSmart = (
  bytes: number,
  binary: boolean = true
): string => {
  if (bytes === 0) return "0 Bytes";

  const k = binary ? 1024 : 1000;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = bytes / Math.pow(k, i);

  // Smart precision: show more decimals for smaller sizes, fewer for larger sizes
  let decimals = 0;
  if (size < 10) decimals = 2;
  else if (size < 100) decimals = 1;
  else decimals = 0;

  return parseFloat(size.toFixed(decimals)) + " " + sizes[i];
};

/**
 * Parses a file size string back to bytes.
 * Supports various formats like "1.5 MB", "500KB", "2.3 GB", etc.
 *
 * @param sizeString - The formatted file size string
 * @param binary - Whether to use binary (1024) or decimal (1000) units (default: false for decimal)
 * @returns The file size in bytes, or 0 if parsing fails
 *
 * @example
 * parseFileSize("1.5 MB") // 1500000
 * parseFileSize("500 KB") // 500000
 * parseFileSize("2.3 GB") // 2300000000
 */
export const parseFileSize = (
  sizeString: string,
  binary: boolean = false
): number => {
  if (!sizeString || typeof sizeString !== "string") return 0;

  const k = binary ? 1024 : 1000;
  const sizes = binary
    ? ["bytes", "kib", "mib", "gib", "tib", "pib", "eib", "zib", "yib"]
    : ["bytes", "kb", "mb", "gb", "tb", "pb", "eb", "zb", "yb"];

  const match = sizeString.toLowerCase().match(/^([\d.]+)\s*([a-z]+)$/);
  if (!match) return 0;

  const value = parseFloat(match[1]);
  const unit = match[2];

  const unitIndex = sizes.findIndex(
    (size) => size === unit || size === unit + "s"
  );
  if (unitIndex === -1) return 0;

  return value * Math.pow(k, unitIndex);
};

/**
 * Gets a human-readable description of file size ranges.
 * Useful for displaying size categories or limits.
 *
 * @param bytes - The file size in bytes
 * @returns A description of the size category
 *
 * @example
 * getFileSizeCategory(1024) // "Small (< 1 MB)"
 * getFileSizeCategory(1048576) // "Medium (1 - 10 MB)"
 * getFileSizeCategory(104857600) // "Large (> 10 MB)"
 */
export const getFileSizeCategory = (bytes: number): string => {
  const mb = bytes / (1000 * 1000);

  if (mb < 1) return "Small (< 1 MB)";
  if (mb < 10) return "Medium (1 - 10 MB)";
  if (mb < 100) return "Large (10 - 100 MB)";
  return "Very Large (> 100 MB)";
};

/**
 * Validates if a file size is within acceptable limits.
 *
 * @param bytes - The file size in bytes
 * @param maxBytes - Maximum allowed file size in bytes (default: 100MB)
 * @returns Object with validation result and message
 *
 * @example
 * validateFileSize(1048576) // { isValid: true, message: "File size is acceptable" }
 * validateFileSize(104857600) // { isValid: false, message: "File size exceeds 100 MB limit" }
 */
export const validateFileSize = (
  bytes: number,
  maxBytes: number = 100 * 1000 * 1000 // 100MB default
): { isValid: boolean; message: string } => {
  if (bytes <= 0) {
    return { isValid: false, message: "Invalid file size" };
  }

  if (bytes > maxBytes) {
    const maxSizeFormatted = formatFileSize(maxBytes);
    return {
      isValid: false,
      message: `File size exceeds ${maxSizeFormatted} limit`,
    };
  }

  return { isValid: true, message: "File size is acceptable" };
};
